// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo, useRef, useState, type ReactNode, useEffect } from 'react';

import { Box } from '@mantine/core';
import ReactDOM from 'react-dom';

import type { MountPortalFn } from 'components/Autodesk/types';

/**
 * Manages a set of React portals that extension UIs can register into the main React tree.
 *
 * Usage: call `mountPortal` from a viewer extension to render React UI inside the existing
 * provider tree (gaining access to MantineProvider, EvolveApiProvider, etc.) without creating
 * a separate `ReactDOM.Root`. The returned cleanup function removes the portal on extension unload.
 *
 * Render `portals` somewhere in the component's JSX.
 */
export const useViewerPortals = () => {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Counter used to generate stable, unique keys for each registration.
  const nextKey = useRef(0);
  const [entries, setEntries] = useState<Map<number, { container: HTMLElement; node: ReactNode }>>(new Map());

  const mountPortal = useCallback<MountPortalFn>((container, node) => {
    const key = nextKey.current++;
    setEntries((prev) => new Map(prev).set(key, { container, node }));
    return () => {
      if (!mounted.current) return;
      setEntries((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    };
  }, []);

  const portals = useMemo(
    () =>
      [...entries.entries()].map(([key, { container, node }]) =>
        ReactDOM.createPortal(<Box className="evolve-viewer-portal">{node}</Box>, container, String(key)),
      ),
    [entries],
  );

  return { mountPortal, portals } as const;
};
